import { useEffect, useId, useState } from 'react';
import { CANVAS_PRESETS } from '@/data/presets';
import { requestResizeArtboard } from '@/lib/artboardSize';
import {
  DEFAULT_DPI,
  LENGTH_UNITS,
  LENGTH_UNIT_LABELS,
  type LengthUnit,
  clampDpi,
  convertLength,
  convertLengthForDpiChange,
  formatPixelPair,
  fromPixels,
  inputStepForUnit,
  toPixels,
} from '@/lib/lengthUnits';

interface ArtboardSizeDialogProps {
  open: boolean;
  width: number;
  height: number;
  onClose: () => void;
}

const MIN_PX = 32;
const MAX_PX = 8000;

export default function ArtboardSizeDialog({
  open,
  width,
  height,
  onClose,
}: ArtboardSizeDialogProps) {
  const titleId = useId();
  const [unit, setUnit] = useState<LengthUnit>('px');
  const [dpi, setDpi] = useState(DEFAULT_DPI);
  const [w, setW] = useState(width);
  const [h, setH] = useState(height);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUnit('px');
    setDpi(DEFAULT_DPI);
    setW(width);
    setH(height);
    setError(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, width, height, onClose]);

  if (!open) return null;

  const widthPx = toPixels(w, unit, dpi);
  const heightPx = toPixels(h, unit, dpi);

  const changeUnit = (next: LengthUnit) => {
    setW(convertLength(w, unit, next, dpi));
    setH(convertLength(h, unit, next, dpi));
    setUnit(next);
    setError(null);
  };

  const changeDpi = (raw: number) => {
    const next = clampDpi(raw);
    setW(convertLengthForDpiChange(w, unit, dpi, next));
    setH(convertLengthForDpiChange(h, unit, dpi, next));
    setDpi(next);
    setError(null);
  };

  const applyFromPixels = (pxW: number, pxH: number) => {
    setW(fromPixels(pxW, unit, dpi));
    setH(fromPixels(pxH, unit, dpi));
    setError(null);
  };

  const apply = () => {
    const nextW = toPixels(w, unit, dpi);
    const nextH = toPixels(h, unit, dpi);
    if (
      nextW < MIN_PX ||
      nextH < MIN_PX ||
      nextW > MAX_PX ||
      nextH > MAX_PX
    ) {
      setError(
        `Size must be between ${MIN_PX} and ${MAX_PX} px (currently ${formatPixelPair(nextW, nextH)}).`
      );
      return;
    }
    requestResizeArtboard(nextW, nextH);
    onClose();
  };

  const step = inputStepForUnit(unit);

  return (
    <div className="ps-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ps-modal-header">
          <h2 id={titleId}>Artboard size</h2>
          <button type="button" className="ps-menu-link" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="ps-modal-body">
          <p className="ps-hint">
            Choose a preset or enter custom dimensions, then click Apply size.
            Objects stay where they are (not cropped).
          </p>

          {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="size-unit-row">
            <label className="ps-field">
              Unit
              <select
                value={unit}
                onChange={(e) => changeUnit(e.target.value as LengthUnit)}
              >
                {LENGTH_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {LENGTH_UNIT_LABELS[u]}
                  </option>
                ))}
              </select>
            </label>
            {unit !== 'px' && (
              <label className="ps-field">
                DPI
                <input
                  type="number"
                  min={72}
                  max={600}
                  step={1}
                  value={dpi}
                  onChange={(e) => changeDpi(Number(e.target.value))}
                />
              </label>
            )}
          </div>

          <div className="ps-props-row" style={{ display: 'flex', gap: 12 }}>
            <label className="ps-field">
              Width ({unit})
              <input
                type="number"
                min={0}
                step={step}
                value={Number.isFinite(w) ? w : 0}
                onChange={(e) => {
                  setW(Number(e.target.value));
                  setError(null);
                }}
              />
            </label>
            <label className="ps-field">
              Height ({unit})
              <input
                type="number"
                min={0}
                step={step}
                value={Number.isFinite(h) ? h : 0}
                onChange={(e) => {
                  setH(Number(e.target.value));
                  setError(null);
                }}
              />
            </label>
          </div>

          <p className="ps-hint size-px-preview">
            ≈ {formatPixelPair(widthPx, heightPx)}
            {unit !== 'px' ? ` at ${clampDpi(dpi)} DPI` : ''}
          </p>

          <h3 style={{ marginTop: 16, fontSize: 13 }}>Presets</h3>
          <div className="ps-props-actions" style={{ flexWrap: 'wrap' }}>
            {CANVAS_PRESETS.filter((p) => p.id !== 'custom').map((preset) => (
              <button
                key={preset.id}
                type="button"
                title={`${preset.width}×${preset.height} px — fills fields; click Apply size to confirm`}
                onClick={() => applyFromPixels(preset.width, preset.height)}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        <footer className="ps-modal-footer">
          <button type="button" className="ps-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ps-btn primary" onClick={apply}>
            Apply size
          </button>
        </footer>
      </div>
    </div>
  );
}
